import { AppModuleCode, PermissionCode, RoleCode, type ProductCode } from '@gb-ticket/shared-types';
import { Loader2Icon } from 'lucide-react';
import { Navigate, Outlet, useLocation } from 'react-router';

import { useSetupStatus } from '@/platform/brand/use-setup';
import { START_PATH } from '@/platform/lib/products';

import { usePresentVoiceText } from '@/platform/voice/use-voice';

import { hasPermission, isModuleEnabled } from './permissions';
import { useMe } from './use-me';

const EINRICHTEN_PATH = '/einrichten';

const MODULE_DISABLED_MESSAGE = 'Dieses Modul ist deaktiviert.';

export function AuditAdminLayout() {
  return (
    <RequireAccess
      module={AppModuleCode.AUDIT}
      permission={PermissionCode.AUDIT_READ}
      deniedMessage="auth.denied.audit"
    />
  );
}

export function CsatReadLayout() {
  return (
    <RequireAccess
      module={AppModuleCode.CSAT}
      permission={PermissionCode.CSAT_READ}
      deniedMessage="auth.denied.csat"
    />
  );
}

/** Platform-owned: gated by permission only, no Helpdesk module flag. */
export function MasterDataLayout() {
  return (
    <RequireAccess
      permission={[PermissionCode.MASTER_DATA_READ, PermissionCode.ARTICLES_READ]}
      deniedMessage="auth.denied.master-data"
    />
  );
}

export function UsersAdminLayout() {
  return (
    <RequireAccess
      permission={PermissionCode.USERS_READ}
      deniedMessage="auth.denied.organisation"
    />
  );
}

export function RolesAdminLayout() {
  return (
    <RequireAccess
      permission={PermissionCode.USERS_ASSIGN_ROLE}
      deniedMessage="auth.denied.roles"
    />
  );
}

export function ProductsAdminLayout() {
  return (
    <RequireAccess
      permission={PermissionCode.PRODUCTS_MANAGE}
      deniedMessage="auth.denied.modules"
    />
  );
}

export function VoiceAdminLayout() {
  return (
    <RequireAccess permission={PermissionCode.VOICE_MANAGE} deniedMessage="auth.voice.denied" />
  );
}

export function TypographyAdminLayout() {
  return (
    <RequireAccess
      permission={PermissionCode.TYPOGRAPHY_MANAGE}
      deniedMessage="auth.typography.denied"
    />
  );
}

export function NotificationStandardAdminLayout() {
  return <RequireAccess role={RoleCode.IT_ADMIN} deniedMessage="platform.auth.denied.action" />;
}

export function TenantsAdminLayout() {
  return (
    <RequireAccess permission={PermissionCode.TENANTS_MANAGE} deniedMessage="auth.denied.tenants" />
  );
}

export function AiAdminLayout() {
  return <RequireAccess permission={PermissionCode.AI_MANAGE} deniedMessage="auth.denied.ai" />;
}

export function MailAdminLayout() {
  return <RequireAccess permission={PermissionCode.MAIL_MANAGE} deniedMessage="auth.denied.mail" />;
}

export function DirectoryAdminLayout() {
  return (
    <RequireAccess
      permission={PermissionCode.DIRECTORY_MANAGE}
      deniedMessage="auth.denied.directory"
    />
  );
}

export function TicketsLayout() {
  return (
    <RequireAccess permission={PermissionCode.TICKETS_READ} deniedMessage="auth.denied.tickets" />
  );
}

export function QueuesLayout() {
  return (
    <RequireAccess
      module={AppModuleCode.QUEUES}
      permission={PermissionCode.TICKETS_READ_ALL}
      deniedMessage="auth.denied.queues"
    />
  );
}

export function ChangesLayout() {
  return (
    <RequireAccess
      module={AppModuleCode.CHANGES}
      permission={PermissionCode.TICKETS_READ_ALL}
      deniedMessage="auth.denied.changes"
    />
  );
}

export function SlaAdminLayout() {
  return (
    <RequireAccess
      module={AppModuleCode.SLA}
      permission={PermissionCode.SLA_MANAGE}
      deniedMessage="auth.denied.sla"
    />
  );
}

export function ApprovalsAdminLayout() {
  return (
    <RequireAccess
      module={AppModuleCode.APPROVALS}
      permission={PermissionCode.APPROVALS_MANAGE}
      deniedMessage="auth.denied.approvals"
    />
  );
}

export function AutomationAdminLayout() {
  return (
    <RequireAccess
      module={AppModuleCode.AUTOMATION}
      permission={PermissionCode.AUTOMATION_READ}
      deniedMessage="auth.denied.automation"
    />
  );
}

export function IntegrationsAdminLayout() {
  return (
    <RequireAccess
      module={AppModuleCode.INTEGRATIONS}
      permission={PermissionCode.INTEGRATIONS_MANAGE}
      deniedMessage="auth.denied.integrations"
    />
  );
}

export function KbLayout() {
  return (
    <RequireAccess
      module={AppModuleCode.KNOWLEDGE}
      permission={PermissionCode.KB_READ}
      deniedMessage="auth.denied.kb"
    />
  );
}

export function AssetsLayout() {
  return (
    <RequireAccess
      module={AppModuleCode.ASSETS}
      permission={PermissionCode.ASSETS_READ}
      deniedMessage="auth.denied.assets"
    />
  );
}

export function StatusLayout() {
  return <RequireAccess module={AppModuleCode.STATUS} allowAnonymous />;
}

export function KbAiLayout() {
  return <RequireAccess module={AppModuleCode.AI} />;
}

export function RequireAuthLayout() {
  return <RequireAccess deniedMessage="auth.sign-in.required" />;
}

export function SettingsManageLayout() {
  return (
    <RequireAccess
      permission={PermissionCode.SETTINGS_MANAGE}
      deniedMessage="auth.denied.settings"
    />
  );
}

/** Product shell from session `products[]`. Fine permissions stay on inner routes. */
export function RequireProductLayout({ product }: { product: ProductCode }) {
  const me = useMe();

  if (me.isPending) {
    return (
      <p className="flex items-center gap-2 text-muted-foreground">
        <Loader2Icon className="size-4 animate-spin" aria-hidden />
        Wird geladen …
      </p>
    );
  }

  if (!me.data?.products.includes(product)) {
    return <Navigate to={START_PATH} replace />;
  }

  return <Outlet />;
}

/** Permission-only gate for product route trees (no Helpdesk module flag). */
export function RequirePermissionLayout({
  permission,
  deniedMessage,
}: {
  permission: PermissionCode;
  deniedMessage: string;
}) {
  return <RequireAccess permission={permission} deniedMessage={deniedMessage} />;
}

export function RequireModuleLayout({
  module,
  permission,
  deniedMessage,
}: {
  module: AppModuleCode;
  permission?: PermissionCode;
  deniedMessage: string;
}) {
  return <RequireAccess module={module} permission={permission} deniedMessage={deniedMessage} />;
}

function hasAnyPermission(
  user: NonNullable<ReturnType<typeof useMe>['data']>,
  permission: PermissionCode | PermissionCode[],
): boolean {
  const codes = Array.isArray(permission) ? permission : [permission];
  return codes.some((code) => hasPermission(user, code));
}

function RequireAccess({
  module,
  permission,
  role,
  deniedMessage = 'auth.denied.generic',
  allowAnonymous = false,
}: {
  module?: AppModuleCode;
  permission?: PermissionCode | PermissionCode[];
  role?: RoleCode;
  deniedMessage?: string;
  allowAnonymous?: boolean;
}) {
  const me = useMe();
  const setup = useSetupStatus();
  const location = useLocation();
  const present = usePresentVoiceText();

  if (me.isPending || setup.isPending) {
    return (
      <p className="flex items-center gap-2 text-muted-foreground">
        <Loader2Icon className="size-4 animate-spin" aria-hidden />
        Wird geladen …
      </p>
    );
  }

  if (!me.data) {
    if (allowAnonymous) {
      return <Outlet />;
    }
    const from = `${location.pathname}${location.search}`;
    return <Navigate to={`/anmelden?from=${encodeURIComponent(from)}`} replace />;
  }

  if (setup.data?.needsSetup) {
    return <Navigate to={EINRICHTEN_PATH} replace />;
  }

  if (module && !isModuleEnabled(me.data, module)) {
    return (
      <section className="mx-auto max-w-md space-y-3 text-center">
        <h1 className="text-2xl font-bold">Modul deaktiviert</h1>
        <p className="text-muted-foreground">{MODULE_DISABLED_MESSAGE}</p>
      </section>
    );
  }

  if (permission && !hasAnyPermission(me.data, permission)) {
    return (
      <section className="mx-auto max-w-md space-y-3 text-center">
        <h1 className="text-2xl font-bold">Kein Zugriff</h1>
        <p className="text-muted-foreground">{present(deniedMessage)}</p>
      </section>
    );
  }

  if (role && !me.data.roles.includes(role)) {
    return (
      <section className="mx-auto max-w-md space-y-3 text-center">
        <h1 className="text-2xl font-bold">Kein Zugriff</h1>
        <p className="text-muted-foreground">{present(deniedMessage)}</p>
      </section>
    );
  }

  return <Outlet />;
}
